import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cn } from '../lib/cn.js';
import { buttonVariants } from './button.js';
import { createOverlayPrimitive } from './overlay.js';
import { useRestoreFocusOnClose, withOpenerCapture } from './use-restore-focus-on-close.js';
// Records where focus was when a controlled `open` flips to true — see the hook.
const Root = withOpenerCapture(AlertDialogPrimitive.Root, 'AlertDialog.Root');
const Trigger = AlertDialogPrimitive.Trigger;
const Portal = AlertDialogPrimitive.Portal;
const Overlay = createOverlayPrimitive(AlertDialogPrimitive.Overlay, 'modal', 'AlertDialog.Overlay');
const Content = React.forwardRef(({ className, onCloseAutoFocus, ...props }, ref) => {
    // State-opened dialogs have no Radix Trigger to return focus to — see the hook.
    const restoreFocus = useRestoreFocusOnClose(onCloseAutoFocus);
    return (_jsxs(AlertDialogPrimitive.Portal, { children: [_jsx(Overlay, {}), _jsx(AlertDialogPrimitive.Content, { ref: ref, className: cn('fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-[var(--radius-4)] border border-[var(--color-border-default)] bg-[var(--color-surface-raised)] p-6 shadow-lg', className), onCloseAutoFocus: restoreFocus, ...props })] }));
});
Content.displayName = 'AlertDialog.Content';
const Header = ({ className, ...props }) => (_jsx("div", { className: cn('flex flex-col gap-1.5 text-left', className), ...props }));
Header.displayName = 'AlertDialog.Header';
const Footer = ({ className, ...props }) => (_jsx("div", { className: cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className), ...props }));
Footer.displayName = 'AlertDialog.Footer';
const Title = React.forwardRef(({ className, ...props }, ref) => (_jsx(AlertDialogPrimitive.Title, { ref: ref, className: cn('text-lg font-semibold text-[var(--color-text-primary)]', className), ...props })));
Title.displayName = 'AlertDialog.Title';
const Description = React.forwardRef(({ className, ...props }, ref) => (_jsx(AlertDialogPrimitive.Description, { ref: ref, className: cn('text-sm text-[var(--color-text-muted)]', className), ...props })));
Description.displayName = 'AlertDialog.Description';
const Action = React.forwardRef(({ className, intent = 'primary', ...props }, ref) => {
    // Destructive confirmations (Delete, Revoke) should visually signal risk.
    // Ship `intent="danger"` so consumers don't hand-paint the red bg and drift
    // from design tokens on the next palette tweak. Reuses buttonVariants so
    // this stays in lockstep with Button instead of hand-copying its classes.
    return (_jsx(AlertDialogPrimitive.Action, { ref: ref, className: cn(buttonVariants({ variant: intent, size: 'md' }), className), ...props }));
});
Action.displayName = 'AlertDialog.Action';
const Cancel = React.forwardRef(({ className, ...props }, ref) => (_jsx(AlertDialogPrimitive.Cancel, { ref: ref, className: cn(buttonVariants({ variant: 'secondary', size: 'md' }), 'border border-[var(--color-border-default)]', className), ...props })));
Cancel.displayName = 'AlertDialog.Cancel';
export const AlertDialog = Object.assign(Root, {
    Root,
    Trigger,
    Portal,
    Overlay,
    Content,
    Header,
    Footer,
    Title,
    Description,
    Action,
    Cancel,
});
//# sourceMappingURL=alert-dialog.js.map